import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Logo } from '../components/EnergyField';
import { AudioToggle } from '../components/AmbientAudio';
import { BookOpen, FileText, Bell, FlaskConical, Upload, Layers, SlidersHorizontal, ArrowLeft, Check, CalendarClock, Send, Mail, RotateCw, Pin, Radar, ExternalLink, X, Share2, GitBranch, Trash2, ShieldCheck, Activity, Plus, Search } from 'lucide-react';
import { CONTENT_TYPES, STATUS_OPTIONS } from '../lib/meta';
import { EvidenceLabel } from '../components/EvidenceLabel';
import { relationshipMeta, RELATIONSHIP_ACTIONS, EVIDENCE_ORDER, evidenceMeta } from '../lib/evidence';

const STRIPE_NA = 'Managed in Stripe';
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fieldClass =
  'w-full bg-[#0c0f17]/60 border border-[#262A36] rounded-lg px-4 py-3 text-sm text-white placeholder-[#4b5563] focus:border-[#1E8F89] focus:outline-none focus:ring-1 focus:ring-[#1E8F89]/40 transition-colors';
const labelClass = 'mono-meta mb-2 block';

const DomainSelect = ({ categories, value, onChange, testid }) => (
  <select value={value} onChange={onChange} required className={fieldClass} data-testid={testid}>
    <option value="" disabled>Select domain</option>
    {categories.map((c) => <option key={c.id} value={c.id} className="bg-[#0c0f17]">{c.name}</option>)}
  </select>
);

const SECTIONS = [
  { id: 'log', label: 'Add Research Log', Icon: BookOpen },
  { id: 'blueprint', label: 'Add Blueprint', Icon: FileText },
  { id: 'experiment', label: 'Add Experiment', Icon: FlaskConical },
  { id: 'update', label: 'Add Field Update', Icon: Bell },
  { id: 'upload', label: 'Upload PDF', Icon: Upload },
  { id: 'scheduling', label: 'Scheduling & Email', Icon: CalendarClock },
  { id: 'ingestion', label: 'Research Ingestion', Icon: Radar },
  { id: 'connections', label: 'Connections', Icon: Share2 },
  { id: 'governance', label: 'Governance', Icon: ShieldCheck },
  { id: 'health', label: 'Archive Health', Icon: Activity },
  { id: 'categories', label: 'Manage Categories', Icon: Layers },
  { id: 'status', label: 'Members', Icon: SlidersHorizontal },
];

const ToggleRow = ({ label, desc, on, onClick, testid }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0">
      <p className="font-sans-tech text-sm text-[#C8CDD6]">{label}</p>
      {desc && <p className="mono-meta text-[#5b626f] mt-0.5">{desc}</p>}
    </div>
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={!!on}
      className={`relative w-11 h-6 rounded-full shrink-0 transition-colors ${on ? 'bg-[#1E8F89]' : 'bg-[#262A36]'}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? 'translate-x-5' : ''}`} />
    </button>
  </div>
);

const Metric = ({ label, value, sub, testid }) => (
  <div className="border border-[#262A36] rounded-xl p-4 bg-[#0c0f17]/40" data-testid={testid}>
    <p className="mono-meta text-[#5b626f]">{label}</p>
    <p className="font-serif-display text-3xl font-light text-[#E2E4E9] mt-1 leading-none">{value}</p>
    {sub && <p className="mono-meta text-[#3a4254] mt-1.5">{sub}</p>}
  </div>
);


const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState('log');

  const [logForm, setLogForm] = useState({ category_id: '', title: '', description: '', content: '', difficulty: 'beginner', content_type: 'Field Note', status: 'EXPERIMENTAL', experiment_phase: '', related_domains: '', references: '', publish_at: '', notify_subscribers: false, is_public: false });
  const [expForm, setExpForm] = useState({ category_id: '', title: '', description: '', content: '', difficulty: 'experimental', content_type: 'Experiment Log', status: 'IN PROGRESS', experiment_phase: '', related_domains: '', references: '', publish_at: '', notify_subscribers: false, is_public: false });
  const [bpForm, setBpForm] = useState({ category_id: '', title: '', description: '', file_url: '', file_size: '', complexity: '', updated: '' });
  const [uploadForm, setUploadForm] = useState({ category_id: '', title: '', description: '', complexity: '', updated: '', file: null });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [updateForm, setUpdateForm] = useState({ title: '', description: '', publish_at: '', notify_subscribers: true, is_public: false });
  const [scheduled, setScheduled] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [ingestQueue, setIngestQueue] = useState([]);
  const [ingestRuns, setIngestRuns] = useState([]);
  const [ingestSources, setIngestSources] = useState({ arxiv: true, semantic_scholar: true });
  const [ingestBusy, setIngestBusy] = useState(false);
  const [linkStatus, setLinkStatus] = useState(null);
  const [linkArticles, setLinkArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState('');
  const [linkData, setLinkData] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [gov, setGov] = useState(null);
  const [govSaving, setGovSaving] = useState(false);
  const [sourceRules, setSourceRules] = useState([]);
  const [newRule, setNewRule] = useState({ pattern: '', kind: 'block', weight: 0.3, label: '' });
  const [health, setHealth] = useState(null);
  const [members, setMembers] = useState(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberFilter, setMemberFilter] = useState('all');
  const [memberSel, setMemberSel] = useState(null);

  const loadCategories = async () => {
    try { const res = await axios.get(`${API}/categories`); setCategories(res.data); } catch (e) { /* ignore */ }
  };

  const loadScheduled = async () => {
    try { const res = await axios.get(`${API}/admin/scheduled`); setScheduled(res.data); } catch (e) { /* ignore */ }
  };

  const loadWeekly = async () => {
    try { const res = await axios.get(`${API}/admin/weekly`); setWeekly(res.data); } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    if (!user.is_admin) { navigate('/dashboard'); return; }
    void Promise.resolve().then(() => { loadCategories(); loadScheduled(); loadWeekly(); });
  }, [user]);

  const rotateWeekly = async () => {
    setEmailBusy(true);
    try { await axios.post(`${API}/admin/weekly/rotate`); toast.success('A new record has been surfaced'); loadWeekly(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Rotation failed'); }
    finally { setEmailBusy(false); }
  };

  const togglePin = async () => {
    if (!weekly) return;
    setEmailBusy(true);
    try {
      if (weekly.weekly_pinned) { await axios.post(`${API}/admin/weekly/unpin`); toast.success('Pin released — rotation resumes'); }
      else { await axios.post(`${API}/admin/weekly/pin/${weekly.id}`); toast.success('Record pinned as this week\u2019s open record'); }
      loadWeekly();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setEmailBusy(false); }
  };

  // Keep the scheduled queue fresh while the admin views it (items auto-publish every minute)
  useEffect(() => {
    if (active !== 'scheduling') return undefined;
    const t = setInterval(() => { loadScheduled(); }, 30000);
    return () => clearInterval(t);
  }, [active]);

  const sendDigest = async () => {
    setEmailBusy(true);
    try { const res = await axios.post(`${API}/admin/send-digest`); toast.success(`Monthly digest sent to ${res.data.sent} subscriber(s)`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Digest failed'); }
    finally { setEmailBusy(false); }
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    try { const res = await axios.post(`${API}/admin/test-email`); toast.success(`Test email sent to ${res.data.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test email failed'); }
    finally { setEmailBusy(false); }
  };

  const loadIngestion = async () => {
    try {
      const [q, r, s] = await Promise.all([
        axios.get(`${API}/admin/ingestion/queue?status=pending`),
        axios.get(`${API}/admin/ingestion/runs`),
        axios.get(`${API}/admin/ingestion/sources`),
      ]);
      setIngestQueue(q.data.items); setIngestRuns(r.data); setIngestSources(s.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (active !== 'ingestion') return;
    void Promise.resolve().then(() => { loadIngestion(); });
  }, [active]);

  const runIngestion = async () => {
    setIngestBusy(true);
    try {
      await axios.post(`${API}/admin/ingestion/run`);
      toast.success('Ingestion run started — grounded drafts will surface shortly');
      setTimeout(loadIngestion, 9000);
      setTimeout(loadIngestion, 22000);
      setTimeout(loadIngestion, 40000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
    finally { setIngestBusy(false); }
  };

  const toggleSource = async (key) => {
    const next = { ...ingestSources, [key]: !ingestSources[key] };
    setIngestSources(next);
    try { await axios.post(`${API}/admin/ingestion/sources`, next); } catch (e) { /* ignore */ }
  };

  const setDraftField = (id, field, value) => {
    setIngestQueue((prev) => prev.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  };

  const approveDraft = async (d) => {
    setIngestBusy(true);
    try {
      const links = (d._suggestions || [])
        .filter((s) => (d._selected || []).includes(s.target_id))
        .map((s) => ({
          target_id: s.target_id, target_type: s.target_type,
          relationship_type: s._relType || s.relationship_type, shared_concepts: s.shared_concepts, score: s.score,
          descriptor: s.descriptor, semantic_similarity: s.semantic_similarity,
          relationship_confidence: s.relationship_confidence, relationship_basis: s.relationship_basis,
        }));
      await axios.post(`${API}/admin/ingestion/${d.id}/approve`, {
        category_id: d.category_id, is_public: !!d._is_public,
        evidence_class: d._evidence_class || d.suggested_evidence_class, links,
      });
      toast.success(`Archived into the research network${links.length ? ` · ${links.length} connection${links.length > 1 ? 's' : ''}` : ''}`);
      setIngestQueue((p) => p.filter((x) => x.id !== d.id));
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setIngestBusy(false); }
  };

  const rejectDraft = async (id) => {
    setIngestBusy(true);
    try { await axios.post(`${API}/admin/ingestion/${id}/reject`); setIngestQueue((p) => p.filter((x) => x.id !== id)); toast.success('Draft dismissed'); }
    catch (e) { toast.error('Reject failed'); }
    finally { setIngestBusy(false); }
  };

  // --- Ingestion approval: suggested connections ---
  const loadDraftSuggestions = async (d) => {
    setDraftField(d.id, '_loadingSug', true);
    try {
      const res = await axios.get(`${API}/admin/ingestion/${d.id}/suggestions`);
      setDraftField(d.id, '_suggestions', res.data.suggestions || []);
      setDraftField(d.id, '_selected', (res.data.suggestions || []).map((s) => s.target_id));
    } catch (e) { toast.error('Could not load connections'); }
    finally { setDraftField(d.id, '_loadingSug', false); }
  };

  const toggleDraftLink = (d, targetId) => {
    const cur = d._selected || [];
    setDraftField(d.id, '_selected', cur.includes(targetId) ? cur.filter((x) => x !== targetId) : [...cur, targetId]);
  };

  // --- Connections section ---
  const loadConnections = async () => {
    try {
      const [st, arts] = await Promise.all([
        axios.get(`${API}/admin/linking/status`),
        axios.get(`${API}/admin/linking/articles`),
      ]);
      setLinkStatus(st.data); setLinkArticles(arts.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (active !== 'connections') return;
    void Promise.resolve().then(() => { loadConnections(); });
  }, [active]);

  const loadMembers = async () => {
    try {
      const params = new URLSearchParams();
      if (memberQuery) params.set('q', memberQuery);
      if (memberFilter && memberFilter !== 'all') params.set('tier', memberFilter);
      const res = await axios.get(`${API}/admin/members?${params.toString()}`);
      setMembers(res.data);
    } catch (e) { toast.error('Could not load members'); }
  };

  useEffect(() => {
    if (active !== 'status') return;
    void Promise.resolve().then(() => { loadMembers(); });
  }, [active, memberFilter]);

  const selectLinkArticle = async (id) => {
    setSelectedArticle(id);
    setLinkData(null);
    if (!id) return;
    try { const res = await axios.get(`${API}/admin/linking/suggestions/${id}`); setLinkData(res.data); }
    catch (e) { toast.error('Could not load suggestions'); }
  };

  const runBackfill = async () => {
    setLinkBusy(true);
    try { await axios.post(`${API}/admin/linking/backfill`); toast.success('Concept backfill started — give it a moment'); setTimeout(loadConnections, 8000); }
    catch (e) { toast.error(e.response?.data?.detail || 'Backfill failed'); }
    finally { setLinkBusy(false); }
  };

  const approveLink = async (s, relType) => {
    setLinkBusy(true);
    try {
      await axios.post(`${API}/admin/linking/approve`, {
        source_id: selectedArticle, source_type: 'article',
        target_id: s.target_id, target_type: s.target_type,
        relationship_type: relType || s.relationship_type, shared_concepts: s.shared_concepts, score: s.score,
        descriptor: s.descriptor, semantic_similarity: s.semantic_similarity,
        relationship_confidence: s.relationship_confidence, relationship_basis: s.relationship_basis,
      });
      toast.success(`Connection approved · ${relationshipMeta(relType || s.relationship_type).label}`);
      selectLinkArticle(selectedArticle);
      loadConnections();
    } catch (e) { toast.error('Approve failed'); }
    finally { setLinkBusy(false); }
  };

  const ignoreLink = async (s) => {
    setLinkBusy(true);
    try { await axios.post(`${API}/admin/linking/ignore`, { source_id: selectedArticle, target_id: s.target_id }); setLinkData((d) => ({ ...d, suggestions: d.suggestions.filter((x) => x.target_id !== s.target_id) })); }
    catch (e) { toast.error('Ignore failed'); }
    finally { setLinkBusy(false); }
  };

  const removeLink = async (linkId) => {
    setLinkBusy(true);
    try { await axios.delete(`${API}/admin/linking/${linkId}`); selectLinkArticle(selectedArticle); loadConnections(); toast.success('Connection removed'); }
    catch (e) { toast.error('Remove failed'); }
    finally { setLinkBusy(false); }
  };

  // --- Governance ---
  const loadGovernance = async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/governance/config`),
        axios.get(`${API}/admin/governance/sources`),
      ]);
      setGov(c.data); setSourceRules(s.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (active !== 'governance') return;
    void Promise.resolve().then(() => { loadGovernance(); });
  }, [active]);

  const saveGov = async (patch) => {
    setGov((g) => ({ ...g, ...patch }));
    setGovSaving(true);
    try { const res = await axios.post(`${API}/admin/governance/config`, patch); setGov(res.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save setting'); loadGovernance(); }
    finally { setGovSaving(false); }
  };

  const addSourceRule = async () => {
    if (!newRule.pattern.trim()) { toast.error('Enter a domain pattern'); return; }
    try {
      await axios.post(`${API}/admin/governance/sources`, newRule);
      setNewRule({ pattern: '', kind: 'block', weight: 0.3, label: '' });
      const s = await axios.get(`${API}/admin/governance/sources`); setSourceRules(s.data);
      toast.success('Source rule added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add rule'); }
  };

  const deleteSourceRule = async (id) => {
    try { await axios.delete(`${API}/admin/governance/sources/${id}`); setSourceRules((r) => r.filter((x) => x.id !== id)); }
    catch (e) { toast.error('Could not remove rule'); }
  };

  // --- Archive Health ---
  const loadHealth = async () => {
    try { const res = await axios.get(`${API}/admin/health`); setHealth(res.data); }
    catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (active !== 'health') return;
    void Promise.resolve().then(() => { loadHealth(); });
  }, [active]);


  const submit = async (url, body, reset, msg) => {
    setLoading(true);
    try { await axios.post(`${API}${url}`, body); toast.success(msg); reset(); loadScheduled(); }
    catch { toast.error('Operation failed'); }
    finally { setLoading(false); }
  };

  const scheduleFields = (form, setForm, prefix) => (
    <div className="border-t border-[#262A36] pt-5 space-y-4">
      <p className="mono-label flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5" /> Scheduling & Notification</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Publish at (optional)</label>
          <input type="datetime-local" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} className={fieldClass} data-testid={`${prefix}-publish-at`} />
          <p className="mono-meta mt-1.5">Leave empty to publish immediately.</p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none mt-1" data-testid={`${prefix}-notify-label`}>
          <input type="checkbox" checked={form.notify_subscribers} onChange={(e) => setForm({ ...form, notify_subscribers: e.target.checked })} className="w-4 h-4 accent-[#1E8F89]" data-testid={`${prefix}-notify`} />
          <span className="font-sans-tech text-sm text-[#C8CDD6]">Email active subscribers on publish</span>
        </label>
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none border-t border-[#262A36] pt-4" data-testid={`${prefix}-public-label`}>
        <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="w-4 h-4 accent-[#1E8F89] mt-0.5" data-testid={`${prefix}-public`} />
        <span>
          <span className="font-sans-tech text-sm text-[#C8CDD6]">Public record — readable on the free tier</span>
          <span className="block mono-meta mt-0.5">Free / logged-out visitors can read this in full. Leave off to keep it Research-Access only.</span>
        </span>
      </label>
    </div>
  );

  const renderBlueprint = (label, ctaLabel, testidPrefix) => (
    <form onSubmit={(e) => { e.preventDefault(); submit('/admin/pdfs', bpForm, () => setBpForm({ category_id: '', title: '', description: '', file_url: '', file_size: '', complexity: '', updated: '' }), 'Blueprint archived'); }} className="space-y-6">
      <h2 className="font-serif-display text-3xl font-light">{label}</h2>
      <p className="font-sans-tech text-sm text-[#6B7280]">Link an externally hosted PDF, or switch to <span className="text-[#26B0A8]">Upload PDF</span> to store a file directly.</p>
      <div><label className={labelClass}>Domain</label>{DomainSelect({ categories, value: bpForm.category_id, onChange: (e) => setBpForm({ ...bpForm, category_id: e.target.value }), testid: `${testidPrefix}-category` })}</div>
      <div><label className={labelClass}>Title</label><input value={bpForm.title} onChange={(e) => setBpForm({ ...bpForm, title: e.target.value })} required className={fieldClass} data-testid={`${testidPrefix}-title`} /></div>
      <div><label className={labelClass}>Description</label><textarea value={bpForm.description} onChange={(e) => setBpForm({ ...bpForm, description: e.target.value })} required rows={3} className={fieldClass} data-testid={`${testidPrefix}-description`} /></div>
      <div><label className={labelClass}>PDF URL</label><input type="url" value={bpForm.file_url} onChange={(e) => setBpForm({ ...bpForm, file_url: e.target.value })} required placeholder="https://…/blueprint.pdf" className={fieldClass} data-testid={`${testidPrefix}-url`} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelClass}>File Size</label><input value={bpForm.file_size} onChange={(e) => setBpForm({ ...bpForm, file_size: e.target.value })} placeholder="3.2 MB" className={fieldClass} data-testid={`${testidPrefix}-size`} /></div>
        <div><label className={labelClass}>Complexity</label><input value={bpForm.complexity} onChange={(e) => setBpForm({ ...bpForm, complexity: e.target.value })} placeholder="Advanced" className={fieldClass} data-testid={`${testidPrefix}-complexity`} /></div>
      </div>
      <div><label className={labelClass}>Last Updated (optional)</label><input value={bpForm.updated} onChange={(e) => setBpForm({ ...bpForm, updated: e.target.value })} placeholder="rev. C · 2026.04" className={fieldClass} data-testid={`${testidPrefix}-updated`} /></div>
      <button type="submit" disabled={loading} className="btn-primary w-full" data-testid={`submit-${testidPrefix}`}>{loading ? 'Archiving…' : ctaLabel}</button>
    </form>
  );

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) { toast.error('Select a PDF file to upload'); return; }
    const fd = new FormData();
    fd.append('file', uploadForm.file);
    fd.append('category_id', uploadForm.category_id);
    fd.append('title', uploadForm.title);
    fd.append('description', uploadForm.description);
    fd.append('complexity', uploadForm.complexity);
    fd.append('updated', uploadForm.updated);
    setLoading(true);
    setUploadProgress(0);
    try {
      await axios.post(`${API}/admin/pdfs/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => { if (p.total) setUploadProgress(Math.round((p.loaded * 100) / p.total)); },
      });
      toast.success('PDF uploaded · blueprint archived');
      setUploadForm({ category_id: '', title: '', description: '', complexity: '', updated: '', file: null });
      setUploadProgress(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const renderUpload = () => (
    <form onSubmit={handleUpload} className="space-y-6">
      <h2 className="font-serif-display text-3xl font-light">Upload PDF</h2>
      <p className="font-sans-tech text-sm text-[#6B7280]">Store a PDF directly in the secure archive. Only active subscribers can retrieve it.</p>
      <div><label className={labelClass}>Domain</label>{DomainSelect({ categories, value: uploadForm.category_id, onChange: (e) => setUploadForm({ ...uploadForm, category_id: e.target.value }), testid: 'upload-category' })}</div>
      <div><label className={labelClass}>Title</label><input value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} required className={fieldClass} data-testid="upload-title" /></div>
      <div><label className={labelClass}>Description</label><textarea value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} required rows={3} className={fieldClass} data-testid="upload-description" /></div>
      <div>
        <label className={labelClass}>PDF File</label>
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
          required
          className="w-full text-sm text-[#949BAA] file:mr-4 file:py-2.5 file:px-5 file:rounded-lg file:border file:border-[#1E8F89]/40 file:bg-[#1E8F89]/10 file:text-[#26B0A8] file:font-mono-tech file:text-xs file:uppercase file:tracking-widest hover:file:bg-[#1E8F89]/20 file:cursor-pointer cursor-pointer"
          data-testid="upload-file"
        />
        {uploadForm.file && <p className="mono-meta mt-2" data-testid="upload-filename">{uploadForm.file.name} · {(uploadForm.file.size / (1024 * 1024)).toFixed(1)} MB</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelClass}>Complexity</label><input value={uploadForm.complexity} onChange={(e) => setUploadForm({ ...uploadForm, complexity: e.target.value })} placeholder="Advanced" className={fieldClass} data-testid="upload-complexity" /></div>
        <div><label className={labelClass}>Last Updated (optional)</label><input value={uploadForm.updated} onChange={(e) => setUploadForm({ ...uploadForm, updated: e.target.value })} placeholder="rev. C · 2026.04" className={fieldClass} data-testid="upload-updated" /></div>
      </div>
      {loading && uploadProgress > 0 && (
        <div className="space-y-2" data-testid="upload-progress">
          <div className="h-1.5 bg-[#0c0f17] rounded-full overflow-hidden border border-[#262A36]">
            <div className="h-full bg-[#1E8F89] transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
          <p className="mono-meta">{uploadProgress}% transferred</p>
        </div>
      )}
      <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="submit-upload">{loading ? 'Uploading…' : 'Upload & Archive PDF'}</button>
    </form>
  );

  const renderLogLike = (form, setForm, lockExperimental, label, ctaLabel, testidPrefix) => {
    const resetState = lockExperimental
      ? { category_id: '', title: '', description: '', content: '', difficulty: 'experimental', content_type: 'Experiment Log', status: 'IN PROGRESS', experiment_phase: '', related_domains: '', references: '', publish_at: '', notify_subscribers: false, is_public: false }
      : { category_id: '', title: '', description: '', content: '', difficulty: 'beginner', content_type: 'Field Note', status: 'EXPERIMENTAL', experiment_phase: '', related_domains: '', references: '', publish_at: '', notify_subscribers: false, is_public: false };
    const onSubmit = (e) => {
      e.preventDefault();
      const payload = {
        ...form,
        experiment_phase: form.experiment_phase || null,
        related_domains: form.related_domains.split(',').map((s) => s.trim()).filter(Boolean),
        references: form.references.split('\n').map((s) => s.trim()).filter(Boolean),
        publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null,
        notify_subscribers: form.notify_subscribers,
      };
      submit('/admin/articles', payload, () => setForm(resetState), lockExperimental ? 'Experiment logged' : 'Research log published');
    };
    return (
    <form onSubmit={onSubmit} className="space-y-6">
      <h2 className="font-serif-display text-3xl font-light">{label}</h2>
      <div><label className={labelClass}>Domain</label>{DomainSelect({ categories, value: form.category_id, onChange: (e) => setForm({ ...form, category_id: e.target.value }), testid: `${testidPrefix}-category` })}</div>
      <div><label className={labelClass}>Title</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className={fieldClass} data-testid={`${testidPrefix}-title`} /></div>
      <div><label className={labelClass}>Summary</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={3} className={fieldClass} data-testid={`${testidPrefix}-description`} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelClass}>Content Type</label>
          <select value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} className={fieldClass} data-testid={`${testidPrefix}-content-type`}>
            {CONTENT_TYPES.map((t) => <option key={t} value={t} className="bg-[#0c0f17]">{t}</option>)}
          </select>
        </div>
        <div><label className={labelClass}>Status</label>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={fieldClass} data-testid={`${testidPrefix}-status`}>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s} className="bg-[#0c0f17]">{s}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelClass}>Classification</label>
          <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className={fieldClass} data-testid={`${testidPrefix}-difficulty`}>
            <option value="beginner" className="bg-[#0c0f17]">Beginner</option>
            <option value="intermediate" className="bg-[#0c0f17]">Field Test</option>
            <option value="advanced" className="bg-[#0c0f17]">Advanced</option>
            <option value="experimental" className="bg-[#0c0f17]">Experimental</option>
          </select>
        </div>
        <div><label className={labelClass}>Experiment Phase (optional)</label><input value={form.experiment_phase} onChange={(e) => setForm({ ...form, experiment_phase: e.target.value })} placeholder="Phase 2 / 4" className={fieldClass} data-testid={`${testidPrefix}-phase`} /></div>
      </div>
      <div><label className={labelClass}>Related Domains (comma-separated, optional)</label><input value={form.related_domains} onChange={(e) => setForm({ ...form, related_domains: e.target.value })} placeholder="Atmospheric Energy, Resonance & Frequency" className={fieldClass} data-testid={`${testidPrefix}-related`} /></div>
      <div><label className={labelClass}>Content · use ``` to wrap data tables / notes</label><textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={14} className={`${fieldClass} font-mono-tech text-xs`} data-testid={`${testidPrefix}-content`} /></div>
      <div><label className={labelClass}>References (one per line, optional)</label><textarea value={form.references} onChange={(e) => setForm({ ...form, references: e.target.value })} rows={3} placeholder={'Author — Title (Year)'} className={fieldClass} data-testid={`${testidPrefix}-references`} /></div>
      {scheduleFields(form, setForm, testidPrefix)}
      <button type="submit" disabled={loading} className="btn-primary w-full" data-testid={`submit-${testidPrefix}`}>{loading ? 'Publishing…' : ctaLabel}</button>
    </form>
    );
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-[#E2E4E9] relative overflow-x-hidden">
      <div className="fixed inset-0 atmos-grid opacity-60 pointer-events-none" aria-hidden="true" />
      <nav className="sticky top-0 z-50 glass-hud border-b border-[#262A36]">
        <div className="container mx-auto px-6 lg:px-12 py-4 flex justify-between items-center gap-4">
          <div className="flex items-center gap-4 min-w-0"><Logo /><span className="tag tag-teal hidden md:inline">Management Console</span></div>
          <div className="flex items-center gap-3 shrink-0">
            <AudioToggle className="hidden sm:flex" />
            <button onClick={() => navigate('/dashboard')} className="btn-quiet !py-2 !px-4 sm:!px-5 shrink-0" data-testid="back-to-dashboard"><ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Interface</span></button>
          </div>
        </div>
      </nav>

      <div className="relative z-10 container mx-auto px-6 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 lg:gap-8">
        <aside className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-6 px-6 lg:mx-0 lg:px-0">
          <p className="mono-label mb-4 hidden lg:block">Console Operations</p>
          {SECTIONS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setActive(id)}
              className={`shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg font-sans-tech text-sm whitespace-nowrap transition-all ${active === id ? 'bg-[#1E8F89]/10 text-[#26B0A8] border border-[#1E8F89]/30' : 'text-[#949BAA] hover:bg-white/[0.03] border border-transparent'}`}
              data-testid={`section-${id}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </aside>

        <div className="glass-hud rounded-xl p-5 sm:p-8 w-full min-w-0 max-w-full lg:max-w-2xl reflect-top">
          {active === 'log' && renderLogLike(logForm, setLogForm, false, 'Add Research Log', 'Publish Research Log', 'log')}
          {active === 'experiment' && renderLogLike(expForm, setExpForm, true, 'Add Experiment', 'Log Experiment', 'experiment')}
          {active === 'blueprint' && renderBlueprint('Add Blueprint', 'Archive Blueprint', 'bp')}
          {active === 'upload' && renderUpload()}

          {active === 'update' && (
            <form onSubmit={(e) => { e.preventDefault(); submit('/admin/updates', { ...updateForm, publish_at: updateForm.publish_at ? new Date(updateForm.publish_at).toISOString() : null }, () => setUpdateForm({ title: '', description: '', publish_at: '', notify_subscribers: true, is_public: false }), updateForm.publish_at ? 'Field transmission scheduled' : 'Field transmission sent'); }} className="space-y-6">
              <h2 className="font-serif-display text-3xl font-light">Add Field Update</h2>
              <div><label className={labelClass}>Title</label><input value={updateForm.title} onChange={(e) => setUpdateForm({ ...updateForm, title: e.target.value })} required className={fieldClass} data-testid="update-title" /></div>
              <div><label className={labelClass}>Transmission</label><textarea value={updateForm.description} onChange={(e) => setUpdateForm({ ...updateForm, description: e.target.value })} required rows={8} className={fieldClass} data-testid="update-description" /></div>
              {scheduleFields(updateForm, setUpdateForm, 'update')}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="submit-update">{loading ? 'Transmitting…' : (updateForm.publish_at ? 'Schedule Field Update' : 'Send Field Update')}</button>
            </form>
          )}

          {active === 'scheduling' && (
            <div className="space-y-7" data-testid="scheduling-section">
              <div>
                <h2 className="font-serif-display text-3xl font-light">Scheduling & Email</h2>
                <p className="font-sans-tech text-sm text-[#6B7280] mt-2">Content auto-publishes at its scheduled time and notifies active subscribers. A monthly digest goes out automatically on the 1st (09:00 UTC).</p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button onClick={sendDigest} disabled={emailBusy} className="btn-primary !py-2.5 !px-5" data-testid="send-digest"><Send className="w-4 h-4" /> {emailBusy ? 'Sending…' : 'Send Monthly Digest Now'}</button>
                <button onClick={sendTestEmail} disabled={emailBusy} className="btn-quiet !py-2.5 !px-5" data-testid="send-test-email"><Mail className="w-4 h-4" /> Send Test Email</button>
              </div>

              <div className="weekly-card" data-testid="admin-weekly">
                <div className="relative z-10">
                  <p className="mono-label mb-3 flex items-center gap-2 text-[#26B0A8]"><RotateCw className="w-3.5 h-3.5" /> Weekly Open Record</p>
                  {weekly ? (
                    <>
                      <h3 className="font-serif-display text-2xl font-light">{weekly.title}</h3>
                      <p className="mono-meta mt-2 text-[#5b626f]">{weekly.content_type || 'Field Note'} · {weekly.weekly_opened_at ? `surfaced ${new Date(weekly.weekly_opened_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'active'}{weekly.weekly_pinned ? ' · pinned' : ''}</p>
                    </>
                  ) : (
                    <p className="font-sans-tech text-sm text-[#6B7280]">No record surfaced yet.</p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-5">
                    <button onClick={rotateWeekly} disabled={emailBusy} className="btn-ambient !py-2 !px-4 !text-[0.68rem]" data-testid="weekly-rotate"><RotateCw className="w-4 h-4" /> Surface a New Record</button>
                    <button onClick={togglePin} disabled={emailBusy || !weekly} className="btn-quiet !py-2 !px-4 !text-[0.68rem]" data-testid="weekly-pin"><Pin className="w-4 h-4" /> {weekly?.weekly_pinned ? 'Unpin (resume rotation)' : 'Pin this record'}</button>
                  </div>
                  <p className="mono-meta mt-4 text-[#5b626f]">Rotates automatically every Monday 08:00 UTC. Pinning holds the current record until released.</p>
                </div>
              </div>

              <div>
                <p className="mono-label mb-4 flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5" /> Scheduled Queue ({scheduled.length})</p>
                {scheduled.length === 0 ? (
                  <div className="border border-[#262A36] rounded-xl py-12 text-center" data-testid="scheduled-empty"><p className="font-sans-tech text-[#6B7280]">Nothing queued. Set a future &ldquo;Publish at&rdquo; on any log or field update.</p></div>
                ) : (
                  <div className="space-y-3" data-testid="scheduled-list">
                    {scheduled.map((s) => (
                      <div key={s.id} className="flex items-center justify-between border border-[#262A36] rounded-lg px-4 py-3" data-testid={`scheduled-${s.id}`}>
                        <div className="min-w-0">
                          <p className="mono-meta text-[#1E8F89]">{s.kind}{s.notify ? ' · notifies' : ''}</p>
                          <p className="font-sans-tech text-[#E2E4E9] truncate">{s.title}</p>
                        </div>
                        <span className="mono-meta shrink-0 ml-4">{new Date(s.publish_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {active === 'ingestion' && (
            <div className="space-y-7" data-testid="ingestion-section">
              <div>
                <h2 className="font-serif-display text-3xl font-light flex items-center gap-3"><Radar className="w-6 h-6 text-[#26B0A8]" /> Research Ingestion</h2>
                <p className="font-sans-tech text-sm text-[#6B7280] mt-2">A daily pipeline quietly scans real scientific sources (arXiv · Semantic Scholar), drafts grounded field-note summaries, and holds them here for your review. Nothing publishes without approval. Restraint by design — small, credible additions over volume.</p>
              </div>

              <div className="border border-[#262A36] rounded-xl p-5 space-y-4">
                <p className="mono-label flex items-center gap-2"><SlidersHorizontal className="w-3.5 h-3.5" /> Sources</p>
                <div className="flex flex-wrap gap-3">
                  {[{ k: 'arxiv', label: 'arXiv' }, { k: 'semantic_scholar', label: 'Semantic Scholar' }].map(({ k, label }) => (
                    <button key={k} onClick={() => toggleSource(k)} data-testid={`ingest-source-${k}`}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-sans-tech text-sm border transition-all ${ingestSources[k] ? 'bg-[#1E8F89]/10 text-[#26B0A8] border-[#1E8F89]/30' : 'text-[#6B7280] border-[#262A36] hover:bg-white/[0.02]'}`}>
                      <span className={`w-2 h-2 rounded-full ${ingestSources[k] ? 'bg-[#26B0A8]' : 'bg-[#3a3f4a]'}`} /> {label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <button onClick={runIngestion} disabled={ingestBusy} className="btn-primary !py-2.5 !px-5" data-testid="ingest-run-now"><Radar className="w-4 h-4" /> {ingestBusy ? 'Scanning…' : 'Run Ingestion Now'}</button>
                  <button onClick={loadIngestion} className="btn-quiet !py-2.5 !px-5" data-testid="ingest-refresh"><RotateCw className="w-4 h-4" /> Refresh</button>
                  <span className="mono-meta text-[#5b626f]">Auto-runs daily · 06:00 UTC</span>
                </div>
                {ingestRuns[0] && (
                  <p className="mono-meta text-[#5b626f] border-t border-[#262A36] pt-3" data-testid="ingest-last-run">
                    Last run: {ingestRuns[0].status} · found {ingestRuns[0].found} · drafted {ingestRuns[0].drafted} · skipped {ingestRuns[0].skipped_dupe} dupes · filtered {ingestRuns[0].filtered_low_quality} low-quality
                  </p>
                )}
              </div>

              <div>
                <p className="mono-label mb-4 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Approval Queue ({ingestQueue.length})</p>
                {ingestQueue.length === 0 ? (
                  <div className="border border-[#262A36] rounded-xl py-12 text-center" data-testid="ingest-empty">
                    <p className="font-sans-tech text-[#6B7280]">No drafts awaiting review. Run the pipeline or wait for the daily scan.</p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="ingest-queue-list">
                    {ingestQueue.map((d) => (
                      <div key={d.id} className="border border-[#262A36] rounded-xl p-5 space-y-4 bg-[#0c0f17]/40" data-testid={`ingest-draft-${d.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="tag tag-steel uppercase">{d.source === 'arxiv' ? 'arXiv' : 'Semantic Scholar'}</span>
                              <span className="tag tag-teal">Q {d.quality_score}/10</span>
                              {d.credibility_score != null && <span className="tag tag-teal">Cred {d.credibility_score}</span>}
                              {d.credibility_label && <span className="mono-meta text-[#26B0A8]">{d.credibility_label}</span>}
                              {(d._evidence_class || d.suggested_evidence_class) && <EvidenceLabel evidenceClass={d._evidence_class || d.suggested_evidence_class} />}
                              <span className="mono-meta text-[#5b626f]">{d.suggested_content_type} · {d.suggested_status}</span>
                            </div>
                            <h3 className="font-serif-display text-xl font-light leading-snug">{d.title}</h3>
                            <p className="mono-meta mt-1 text-[#5b626f]">{(d.authors || []).slice(0, 4).join(', ') || 'n/a'}{d.published_date ? ` · ${d.published_date}` : ''}</p>
                          </div>
                          {d.source_url && <a href={d.source_url} target="_blank" rel="noreferrer" className="shrink-0 text-[#6B7280] hover:text-[#26B0A8] transition-colors" data-testid={`ingest-source-link-${d.id}`}><ExternalLink className="w-4 h-4" /></a>}
                        </div>

                        <p className="font-sans-tech text-sm text-[#C8CDD6] leading-relaxed whitespace-pre-line">{(d.ai_summary || '').slice(0, 520)}{(d.ai_summary || '').length > 520 ? '…' : ''}</p>
                        {d.credibility_note && <p className="mono-meta text-[#5b626f]">Credibility: {d.credibility_note}</p>}
                        {(d.suggested_related_domains || []).length > 0 && (
                          <p className="mono-meta text-[#5b626f]">Adjacent: {d.suggested_related_domains.join(' · ')}</p>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-[#262A36] pt-4">
                          <div>
                            <label className={labelClass}>Domain</label>
                            <DomainSelect categories={categories} value={d.category_id} onChange={(e) => setDraftField(d.id, 'category_id', e.target.value)} testid={`ingest-domain-${d.id}`} />
                          </div>
                          <div>
                            <label className={labelClass}>Evidence class · AI recommends {d.suggested_evidence_class || '—'}</label>
                            <select value={d._evidence_class || d.suggested_evidence_class || ''} onChange={(e) => setDraftField(d.id, '_evidence_class', e.target.value)} className={fieldClass} data-testid={`ingest-evidence-${d.id}`}>
                              {EVIDENCE_ORDER.map((k) => <option key={k} value={k} className="bg-[#0c0f17]">{evidenceMeta(k)?.label || k}</option>)}
                            </select>
                          </div>
                          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid={`ingest-public-label-${d.id}`}>
                            <input type="checkbox" checked={!!d._is_public} onChange={(e) => setDraftField(d.id, '_is_public', e.target.checked)} className="w-4 h-4 accent-[#1E8F89]" data-testid={`ingest-public-${d.id}`} />
                            <span className="font-sans-tech text-sm text-[#C8CDD6]">Publish as public record</span>
                          </label>
                        </div>

                        <div className="border-t border-[#262A36] pt-4">
                          {!d._suggestions ? (
                            <button onClick={() => loadDraftSuggestions(d)} disabled={d._loadingSug} className="btn-quiet !py-2 !px-4 !text-[0.68rem]" data-testid={`ingest-suggest-${d.id}`}>
                              <Share2 className="w-4 h-4" /> {d._loadingSug ? 'Scanning archive…' : 'Suggest Connections'}
                            </button>
                          ) : d._suggestions.length === 0 ? (
                            <p className="mono-meta text-[#5b626f]">No archive connections found for this record.</p>
                          ) : (
                            <div>
                              <p className="mono-label mb-2 flex items-center gap-2"><GitBranch className="w-3.5 h-3.5" /> Suggested Connections · {(d._selected || []).length}/{d._suggestions.length} selected</p>
                              <div className="space-y-2">
                                {d._suggestions.map((s) => {
                                  const sel = (d._selected || []).includes(s.target_id);
                                  return (
                                    <button key={s.target_id} type="button" onClick={() => toggleDraftLink(d, s.target_id)} data-testid={`ingest-link-${s.target_id}`}
                                      className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-all ${sel ? 'border-[#1E8F89]/40 bg-[#1E8F89]/[0.06]' : 'border-[#262A36] hover:bg-white/[0.02]'}`}>
                                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 mt-0.5 ${sel ? 'bg-[#1E8F89] border-[#1E8F89]' : 'border-[#3a4254]'}`}>{sel && <Check className="w-3 h-3 text-[#06231f]" />}</span>
                                      <span className="min-w-0">
                                        <span className="block font-sans-tech text-sm text-[#C8CDD6] truncate">{s.target_title}</span>
                                        <span className="mono-meta text-[#26B0A8]">{relationshipMeta(s.relationship_type).label}{s.target_type === 'pdf' ? ' · Blueprint' : ''} · sim {s.semantic_similarity}% · conf {s.relationship_confidence}/100</span>
                                        {s.challenge_note && <span className="block mono-meta text-[#5b626f] mt-0.5 leading-relaxed">⚠ {s.challenge_note}</span>}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-3">
                          <button onClick={() => approveDraft(d)} disabled={ingestBusy} className="btn-primary !py-2 !px-5" data-testid={`ingest-approve-${d.id}`}><Check className="w-4 h-4" /> Approve & Archive</button>
                          <button onClick={() => rejectDraft(d.id)} disabled={ingestBusy} className="btn-quiet !py-2 !px-5" data-testid={`ingest-reject-${d.id}`}><X className="w-4 h-4" /> Dismiss</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {active === 'connections' && (
            <div className="space-y-7" data-testid="connections-section">
              <div>
                <h2 className="font-serif-display text-3xl font-light flex items-center gap-3"><Share2 className="w-6 h-6 text-[#26B0A8]" /> Archive Connections</h2>
                <p className="font-sans-tech text-sm text-[#6B7280] mt-2">The archive discovering its own internal structure. Concept-based suggestions surface links between records — you approve or ignore each one. Nothing links automatically.</p>
              </div>

              <div className="border border-[#262A36] rounded-xl p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={runBackfill} disabled={linkBusy} className="btn-quiet !py-2.5 !px-5" data-testid="linking-backfill"><RotateCw className="w-4 h-4" /> {linkBusy ? 'Working…' : 'Re-scan Concepts'}</button>
                  {linkStatus && <span className="mono-meta text-[#5b626f]" data-testid="linking-status">{linkStatus.articles_with_concepts}/{linkStatus.articles_total} records indexed · {linkStatus.links_total} connections</span>}
                </div>
              </div>

              <div>
                <label className={labelClass}>Select a record</label>
                <select value={selectedArticle} onChange={(e) => selectLinkArticle(e.target.value)} className={fieldClass} data-testid="connections-article-select">
                  <option value="">Choose an archive record…</option>
                  {linkArticles.map((a) => <option key={a.id} value={a.id} className="bg-[#0c0f17]">{a.title}{!a.has_concepts ? ' (no concepts)' : ''}</option>)}
                </select>
              </div>

              {linkData && (
                <>
                  {linkData.approved.length > 0 && (
                    <div data-testid="connections-approved">
                      <p className="mono-label mb-3 flex items-center gap-2"><Check className="w-3.5 h-3.5" /> Approved Connections ({linkData.approved.length})</p>
                      <div className="space-y-2">
                        {linkData.approved.map((l) => (
                          <div key={l.link_id} className="flex items-center justify-between gap-3 border border-[#1E8F89]/25 bg-[#1E8F89]/[0.04] rounded-lg px-4 py-3" data-testid={`approved-link-${l.link_id}`}>
                            <div className="min-w-0">
                              <p className="font-sans-tech text-sm text-[#C8CDD6] truncate">{l.target_title}</p>
                              <p className="mono-meta text-[#26B0A8]">{relationshipMeta(l.relationship_type).label}{typeof l.relationship_confidence === 'number' ? ` · conf ${l.relationship_confidence}/100` : ''}</p>
                            </div>
                            <button onClick={() => removeLink(l.link_id)} disabled={linkBusy} className="text-[#6B7280] hover:text-[#e06363] transition-colors shrink-0" data-testid={`remove-link-${l.link_id}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div data-testid="connections-suggestions">
                    <p className="mono-label mb-3 flex items-center gap-2"><GitBranch className="w-3.5 h-3.5" /> Suggested Connections ({linkData.suggestions.length})</p>
                    {linkData.suggestions.length === 0 ? (
                      <div className="border border-[#262A36] rounded-xl py-10 text-center" data-testid="suggestions-empty"><p className="font-sans-tech text-[#6B7280]">No new connections suggested for this record.</p></div>
                    ) : (
                      <div className="space-y-3">
                        {linkData.suggestions.map((s) => (
                          <div key={s.target_id} className="border border-[#262A36] rounded-xl p-4 bg-[#0c0f17]/40" data-testid={`suggestion-${s.target_id}`}>
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="min-w-0">
                                <p className="font-sans-tech text-sm text-[#E2E4E9]">{s.target_title}</p>
                                <p className="mono-meta text-[#5b626f] mt-0.5">{s.target_domain}{s.target_type === 'pdf' ? ' · Blueprint' : ''}{s.descriptor ? ` · ${s.descriptor}` : ''}</p>
                              </div>
                              <div className="shrink-0 text-right space-y-1">
                                <span className="tag tag-steel block">sim {s.semantic_similarity}%</span>
                                <span className="mono-meta text-[#5b626f] block">conf {s.relationship_confidence}/100</span>
                              </div>
                            </div>
                            <p className="mono-meta text-[#26B0A8] mb-1">Proposed · {relationshipMeta(s.relationship_type).label}</p>
                            {s.relationship_basis && <p className="mono-meta text-[#8a93a3] mb-1 leading-relaxed">{s.relationship_basis}</p>}
                            {s.challenge_note && <p className="mono-meta text-[#5b626f] mb-2 leading-relaxed">⚠ Null-model check: {s.challenge_note}</p>}
                            {s.shared_concepts.length > 0 && <p className="mono-meta text-[#5b626f] mb-3">shared: {s.shared_concepts.join(' · ')}</p>}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <label className="mono-meta text-[#5b626f]">Classify as</label>
                              <select
                                value={s._relType || s.relationship_type}
                                onChange={(e) => setLinkData((d) => ({ ...d, suggestions: d.suggestions.map((x) => x.target_id === s.target_id ? { ...x, _relType: e.target.value } : x) }))}
                                className="bg-[#0c0f17]/60 border border-[#262A36] rounded-lg px-3 py-1.5 text-xs text-white focus:border-[#1E8F89] focus:outline-none"
                                data-testid={`suggestion-reltype-${s.target_id}`}
                              >
                                {RELATIONSHIP_ACTIONS.map((r) => <option key={r} value={r} className="bg-[#0c0f17]">{relationshipMeta(r).label}</option>)}
                              </select>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <button onClick={() => approveLink(s, s._relType || s.relationship_type)} disabled={linkBusy} className="btn-primary !py-1.5 !px-4 !text-[0.68rem]" data-testid={`approve-suggestion-${s.target_id}`}><Check className="w-3.5 h-3.5" /> Approve</button>
                              <button onClick={() => approveLink(s, 'HYPOTHESIS')} disabled={linkBusy} className="btn-quiet !py-1.5 !px-4 !text-[0.68rem]" data-testid={`hypothesis-suggestion-${s.target_id}`}>Mark hypothesis</button>
                              <button onClick={() => approveLink(s, 'INSUFFICIENT')} disabled={linkBusy} className="btn-quiet !py-1.5 !px-4 !text-[0.68rem]" data-testid={`insufficient-suggestion-${s.target_id}`}>Insufficient</button>
                              <button onClick={() => ignoreLink(s)} disabled={linkBusy} className="btn-quiet !py-1.5 !px-4 !text-[0.68rem]" data-testid={`ignore-suggestion-${s.target_id}`}><X className="w-3.5 h-3.5" /> Reject</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {active === 'governance' && gov && (
            <div className="space-y-7" data-testid="governance-section">
              <div>
                <h2 className="font-serif-display text-3xl font-light flex items-center gap-3"><ShieldCheck className="w-6 h-6 text-[#26B0A8]" /> Governance</h2>
                <p className="font-sans-tech text-sm text-[#6B7280] mt-2">Tune the autonomous instrument — cadence, quality &amp; credibility floors, source trust, and opt-in automation. Defaults are restrained and human-first.{govSaving ? ' · saving…' : ''}</p>
              </div>

              <div className="border border-[#262A36] rounded-xl p-5 space-y-5">
                <p className="mono-label flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5" /> Ingestion Cadence</p>
                <div className="flex gap-2">
                  {['daily', 'weekly', 'manual'].map((f) => (
                    <button key={f} onClick={() => saveGov({ frequency: f })} data-testid={`gov-freq-${f}`}
                      className={`px-4 py-2 rounded-lg font-sans-tech text-sm border capitalize transition-all ${gov.frequency === f ? 'bg-[#1E8F89]/10 text-[#26B0A8] border-[#1E8F89]/30' : 'text-[#6B7280] border-[#262A36] hover:bg-white/[0.02]'}`}>{f}</button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-5 pt-1">
                  <div>
                    <label className={labelClass}>Max records / run · {gov.max_per_run}</label>
                    <input type="range" min="1" max="12" value={gov.max_per_run} data-testid="gov-maxrun"
                      onChange={(e) => setGov((g) => ({ ...g, max_per_run: Number(e.target.value) }))} onMouseUp={(e) => saveGov({ max_per_run: Number(e.target.value) })} className="w-full accent-[#1E8F89]" />
                  </div>
                  <div>
                    <label className={labelClass}>Duplicate sensitivity</label>
                    <div className="flex gap-2">
                      {['loose', 'normal', 'strict'].map((s) => (
                        <button key={s} onClick={() => saveGov({ dup_sensitivity: s })} data-testid={`gov-dup-${s}`}
                          className={`flex-1 px-3 py-1.5 rounded-lg font-sans-tech text-xs border capitalize transition-all ${gov.dup_sensitivity === s ? 'bg-[#1E8F89]/10 text-[#26B0A8] border-[#1E8F89]/30' : 'text-[#6B7280] border-[#262A36]'}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Quality floor · {gov.quality_threshold}/10</label>
                    <input type="range" min="1" max="10" value={gov.quality_threshold} data-testid="gov-quality"
                      onChange={(e) => setGov((g) => ({ ...g, quality_threshold: Number(e.target.value) }))} onMouseUp={(e) => saveGov({ quality_threshold: Number(e.target.value) })} className="w-full accent-[#1E8F89]" />
                  </div>
                  <div>
                    <label className={labelClass}>Credibility floor · {gov.credibility_threshold}/100</label>
                    <input type="range" min="0" max="100" value={gov.credibility_threshold} data-testid="gov-credibility"
                      onChange={(e) => setGov((g) => ({ ...g, credibility_threshold: Number(e.target.value) }))} onMouseUp={(e) => saveGov({ credibility_threshold: Number(e.target.value) })} className="w-full accent-[#1E8F89]" />
                  </div>
                </div>
                <ToggleRow label="Auto-archive low-quality drafts" desc="Drop below-threshold records instead of queueing them." on={gov.auto_archive_low_quality} onClick={() => saveGov({ auto_archive_low_quality: !gov.auto_archive_low_quality })} testid="gov-autoarchive" />
              </div>

              <div className="border border-[#262A36] rounded-xl p-5 space-y-4">
                <p className="mono-label flex items-center gap-2"><Radar className="w-3.5 h-3.5" /> Partial Automation · human-first</p>
                <p className="mono-meta text-[#5b626f]">Off by default. Automation assists curation — speculative and theoretical records always route to human review.</p>
                <ToggleRow label="Auto-approve high-credibility records" on={gov.auto_approve_enabled} onClick={() => saveGov({ auto_approve_enabled: !gov.auto_approve_enabled })} testid="gov-autoapprove" />
                {gov.auto_approve_enabled && (
                  <div className="pl-4 border-l border-[#262A36] space-y-4">
                    <div>
                      <label className={labelClass}>Min credibility to auto-approve · {gov.auto_approve_min_credibility}</label>
                      <input type="range" min="60" max="100" value={gov.auto_approve_min_credibility} data-testid="gov-autoapprove-min"
                        onChange={(e) => setGov((g) => ({ ...g, auto_approve_min_credibility: Number(e.target.value) }))} onMouseUp={(e) => saveGov({ auto_approve_min_credibility: Number(e.target.value) })} className="w-full accent-[#1E8F89]" />
                    </div>
                    <ToggleRow label="Trusted sources only" on={gov.auto_approve_trusted_only} onClick={() => saveGov({ auto_approve_trusted_only: !gov.auto_approve_trusted_only })} testid="gov-trustedonly" />
                  </div>
                )}
                <ToggleRow label="Auto-surface as public records" desc="Make auto-approved high-credibility records publicly visible." on={gov.auto_surface_enabled} onClick={() => saveGov({ auto_surface_enabled: !gov.auto_surface_enabled })} testid="gov-autosurface" />
                {gov.auto_surface_enabled && (
                  <div className="pl-4 border-l border-[#262A36]">
                    <label className={labelClass}>Min credibility to surface · {gov.auto_surface_min_credibility}</label>
                    <input type="range" min="70" max="100" value={gov.auto_surface_min_credibility} data-testid="gov-autosurface-min"
                      onChange={(e) => setGov((g) => ({ ...g, auto_surface_min_credibility: Number(e.target.value) }))} onMouseUp={(e) => saveGov({ auto_surface_min_credibility: Number(e.target.value) })} className="w-full accent-[#1E8F89]" />
                  </div>
                )}
                <ToggleRow label="Auto-link semantic connections" desc="On approval, auto-create high-confidence archive links." on={gov.auto_link_enabled} onClick={() => saveGov({ auto_link_enabled: !gov.auto_link_enabled })} testid="gov-autolink" />
              </div>

              <div className="border border-[#262A36] rounded-xl p-5 space-y-4">
                <p className="mono-label flex items-center gap-2"><SlidersHorizontal className="w-3.5 h-3.5" /> Source Trust Map</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <input value={newRule.pattern} onChange={(e) => setNewRule((r) => ({ ...r, pattern: e.target.value }))} placeholder="domain pattern · e.g. example.com" className={`${fieldClass} flex-1 min-w-[180px]`} data-testid="gov-rule-pattern" />
                  <select value={newRule.kind} onChange={(e) => setNewRule((r) => ({ ...r, kind: e.target.value }))} className={fieldClass} data-testid="gov-rule-kind">
                    <option value="allow" className="bg-[#0c0f17]">allow</option>
                    <option value="block" className="bg-[#0c0f17]">block</option>
                  </select>
                  <input type="number" min="0" max="1" step="0.05" value={newRule.weight} onChange={(e) => setNewRule((r) => ({ ...r, weight: Number(e.target.value) }))} className={`${fieldClass} w-24`} data-testid="gov-rule-weight" />
                  <button onClick={addSourceRule} className="btn-primary !py-2.5 !px-4" data-testid="gov-rule-add"><Plus className="w-4 h-4" /> Add</button>
                </div>
                <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1" data-testid="gov-rules-list">
                  {sourceRules.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-[#262A36]" data-testid={`gov-rule-${r.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`tag ${r.kind === 'block' ? 'tag-muted' : 'tag-teal'} uppercase`}>{r.kind}</span>
                        <span className="font-sans-tech text-sm text-[#C8CDD6] truncate">{r.pattern}</span>
                        <span className="mono-meta text-[#5b626f]">trust {r.weight}{r.builtin ? ' · builtin' : ''}</span>
                      </div>
                      <button onClick={() => deleteSourceRule(r.id)} className="text-[#6B7280] hover:text-[#e06363] transition-colors shrink-0" data-testid={`gov-rule-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {active === 'health' && (
            <div className="space-y-7" data-testid="health-section">
              <div>
                <h2 className="font-serif-display text-3xl font-light flex items-center gap-3"><Activity className="w-6 h-6 text-[#26B0A8]" /> Archive Health</h2>
                <p className="font-sans-tech text-sm text-[#6B7280] mt-2">A quiet read on the instrument — ingestion reliability, archive depth, credibility, and where the network is growing or going stale.</p>
              </div>
              {!health ? (
                <p className="mono-meta text-[#5b626f]">Reading metrics…</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="health-metrics">
                    <Metric label="Ingestion success" value={`${health.ingestion.success_rate}%`} sub={`${health.ingestion.completed}/${health.ingestion.total_runs} runs`} testid="metric-success" />
                    <Metric label="Duplicate rejection" value={`${health.ingestion.duplicate_rejection_rate}%`} sub={`${health.ingestion.total_found} found`} testid="metric-dupe" />
                    <Metric label="Avg credibility" value={health.archive.avg_credibility ?? '—'} sub={`${health.archive.ingested_with_credibility} scored`} testid="metric-cred" />
                    <Metric label="Source diversity" value={health.source_diversity} sub="distinct hosts" testid="metric-diversity" />
                    <Metric label="Archive records" value={health.archive.total_records} sub={`+${health.archive.growth_30d} / 30d`} testid="metric-records" />
                    <Metric label="Connections" value={health.semantic.connections} sub={`density ${health.semantic.connection_density}`} testid="metric-connections" />
                    <Metric label="Queue pending" value={health.queue.pending} sub={`${health.queue.approved} approved`} testid="metric-queue" />
                    <Metric label="Records / 7d" value={health.archive.growth_7d} sub="recent growth" testid="metric-growth7" />
                  </div>
                  <div className="border border-[#262A36] rounded-xl p-5">
                    <p className="mono-label mb-4 flex items-center gap-2"><Layers className="w-3.5 h-3.5" /> Domain Activity</p>
                    <div className="space-y-2">
                      {health.domains.map((d) => (
                        <div key={d.domain} className="flex items-center justify-between gap-3 py-1.5 border-b border-[#262A36]/50 last:border-0" data-testid={`health-domain-${d.domain.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                          <span className="font-sans-tech text-sm text-[#C8CDD6]">{d.domain}</span>
                          <div className="flex items-center gap-3">
                            <span className="mono-meta text-[#5b626f]">{d.count} records</span>
                            {d.stale ? <span className="tag tag-muted">stale</span> : <span className="tag tag-teal">active</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {active === 'categories' && (
            <div className="space-y-5">
              <h2 className="font-serif-display text-3xl font-light">Manage Categories</h2>
              <p className="font-sans-tech text-sm text-[#6B7280]">Research domains currently configured.</p>
              <div className="space-y-3" data-testid="categories-list">
                {categories.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border border-[#262A36] rounded-lg px-4 py-3">
                    <div><p className="font-sans-tech text-[#E2E4E9]">{c.name}</p><p className="font-sans-tech text-xs text-[#6B7280]">{c.description}</p></div>
                    <span className="mono-meta">#{c.order}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {active === 'status' && (
            <div className="space-y-6" data-testid="members-panel">
              <div>
                <h2 className="font-serif-display text-3xl font-light">Members</h2>
                <p className="mono-meta text-[#5b626f] mt-1">Operational overview · Stripe remains the source of truth for billing</p>
              </div>

              {!members ? (
                <p className="font-sans-tech text-sm text-[#6B7280]">Loading members…</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="members-summary">
                    {[
                      { k: 'total_accounts', label: 'Total accounts', tint: '#57708f' },
                      { k: 'active_research_access', label: 'Research Access', tint: '#3E7C78' },
                      { k: 'free_accounts', label: 'Free', tint: '#5b626f' },
                      { k: 'admin_accounts', label: 'Admin (unbilled)', tint: '#6d5f80' },
                      { k: 'past_due', label: 'Past due', tint: '#7a6f4a' },
                      { k: 'cancelled', label: 'Cancelled', tint: '#4a4a52' },
                    ].map((m) => (
                      <div key={m.k} className="glass-panel rounded-xl px-4 py-4 border-l-2" style={{ borderLeftColor: m.tint }} data-testid={`metric-${m.k}`}>
                        <p className="font-serif-display text-3xl font-light text-white">{members.summary[m.k] ?? 0}</p>
                        <p className="mono-meta text-[#5b626f] mt-1">{m.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="flex items-center gap-2 flex-1 min-w-0 glass-panel rounded-lg px-3 py-2">
                      <Search className="w-4 h-4 text-[#6B7280] shrink-0" />
                      <input value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadMembers()} placeholder="Search email…" className="bg-transparent border-0 outline-none text-sm text-white w-full font-sans-tech" data-testid="members-search" />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {['all', 'active', 'free', 'admin'].map((f) => (
                        <button key={f} onClick={() => setMemberFilter(f)} className={`shrink-0 px-3 py-1.5 rounded-lg mono-meta uppercase transition-all ${memberFilter === f ? 'bg-[#1E8F89]/10 text-[#26B0A8] border border-[#1E8F89]/30' : 'text-[#949BAA] border border-[#262A36]'}`} data-testid={`members-filter-${f}`}>{f}</button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2" data-testid="members-list">
                    {members.members.length === 0 && <p className="font-sans-tech text-sm text-[#6B7280] py-4">No members match.</p>}
                    {members.members.map((m) => (
                      <button key={m.id} onClick={() => setMemberSel(memberSel?.id === m.id ? null : m)} className="w-full text-left glass-panel rounded-xl px-4 py-3.5 hover:border-[#1E8F89]/30 transition-all" data-testid={`member-${m.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <p className="font-sans-tech text-sm text-[#E2E4E9] break-all">{m.email}</p>
                            <p className="mono-meta text-[#5b626f] mt-0.5">Joined {fmtDate(m.joined)}{m.last_payment_at ? ` · last payment ${fmtDate(m.last_payment_at)}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`tag ${m.tier === 'admin' ? 'tag-violet' : m.tier === 'research_access' ? 'tag-teal' : 'tag-steel'}`}>{m.tier === 'research_access' ? 'Research Access' : m.tier === 'admin' ? 'Admin' : 'Free'}</span>
                          </div>
                        </div>
                        {memberSel?.id === m.id && (
                          <div className="mt-4 pt-4 border-t border-[#262A36] grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid={`member-detail-${m.id}`}>
                            {[
                              ['Role', m.is_admin ? 'Admin' : 'Member'],
                              ['Access', m.tier === 'research_access' ? 'Research Access' : m.tier === 'admin' ? 'Admin (permanent, unbilled)' : 'Free'],
                              ['Subscription status', m.subscription_status],
                              ['Joined', fmtDate(m.joined)],
                              ['Activated', m.subscription_activated_at ? fmtDate(m.subscription_activated_at) : STRIPE_NA],
                              ['Current period end', m.current_period_end ? fmtDate(m.current_period_end) : STRIPE_NA],
                              ['Cancel at period end', m.cancel_at_period_end == null ? STRIPE_NA : String(m.cancel_at_period_end)],
                              ['Cancelled at', m.canceled_at ? fmtDate(m.canceled_at) : STRIPE_NA],
                              ['Stripe customer', m.stripe_customer_id || STRIPE_NA],
                              ['Stripe subscription', m.stripe_subscription_id || STRIPE_NA],
                              ['Last webhook sync', m.last_stripe_webhook_at ? fmtDate(m.last_stripe_webhook_at) : STRIPE_NA],
                              ['Last state change', m.last_subscription_state_change ? fmtDate(m.last_subscription_state_change) : STRIPE_NA],
                            ].map(([label, val]) => (
                              <div key={label} className="flex justify-between gap-3">
                                <span className="mono-meta text-[#5b626f] shrink-0">{label}</span>
                                <span className={`font-sans-tech text-xs text-right break-all ${val === STRIPE_NA ? 'text-[#4a4a52] italic' : 'text-[#C8CDD6]'}`}>{val}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="mono-meta text-[#5b626f] pt-2 leading-relaxed border-t border-[#262A36]">
                    Admin accounts are excluded from paid subscriber counts. Fields marked “{STRIPE_NA}” are governed in Stripe and mirror here for new or renewing subscriptions going forward. This view is read-only.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
